import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Textarea } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { NumericKeypad } from '../../../../components/Keypad/NumericKeypad';
import { appendDigit, appendDot, bufferToAmount, dropLastChar } from '../../../../components/Keypad/amount-buffer.utils';
import keypadClasses from '../../../../components/Keypad/keypad-modal.module.scss';
import { CashMoveSlip } from './CashMoveSlip';
import classes from './cash-move-modal.module.scss';

const MAX_AMOUNT = 999999; // mirror the backend DTO bound (amount.max)
const MAX_REASON = 120; // mirror the backend DTO bound (reason.max)
const TITLE = 'מזומן נכנס / יוצא';
const DIRECTIONS = [['out', 'מזומן יוצא'], ['in', 'מזומן נכנס']] as const;

export type CashMoveDirection = 'in' | 'out';

export type CashMoveModalProps = {
  isOpen: boolean;
  drawerName: string | null;
  header: string;
  employeeName: string | null;
  slipRef?: React.Ref<HTMLDivElement>;
  isSaving?: boolean;
  onConfirm: (direction: CashMoveDirection, amount: number, reason: string) => void | Promise<void>;
  onClose: () => void;
};

// Summary: Manager cash in/out dialog — direction toggle + keypad amount + mandatory reason (RTL).
const CashMoveModalComponent: React.FC<CashMoveModalProps> = ({
  isOpen, drawerName, header, employeeName, slipRef, isSaving = false, onConfirm, onClose,
}) => {
  const [direction, setDirection] = useState<CashMoveDirection>('out');
  const [buffer, setBuffer] = useState('');
  const [reason, setReason] = useState('');
  const [slipAt, setSlipAt] = useState<string | null>(null);
  const confirmInFlightRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setDirection('out');
      setBuffer('');
      setReason('');
      setSlipAt(null);
      confirmInFlightRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const amount = bufferToAmount(buffer);
  const trimmedReason = reason.trim();
  const isValid = Number.isFinite(amount) && amount > 0 && amount <= MAX_AMOUNT && trimmedReason.length > 0;

  const handleConfirm = () => {
    if (!isValid || isSaving || confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    // Commit the slip synchronously so its DOM exists before the caller captures it for printing.
    flushSync(() => setSlipAt(new Date().toISOString()));
    // Clear the lock on every settle so a failed POST (modal stays open) can be retried.
    void Promise.resolve(onConfirm(direction, amount, trimmedReason)).finally(() => {
      confirmInFlightRef.current = false;
    });
  };

  return (
    <div
      className={keypadClasses.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={TITLE}
      onContextMenu={(event) => event.preventDefault()}
      data-testid="cash-move-modal"
    >
      <div className={classes.modal}>
        <div className={keypadClasses.header}>
          <h2 className={keypadClasses.title}>{TITLE}</h2>
        </div>

        <div className={keypadClasses.body}>
          <div className={classes.toggleRow}>
            {DIRECTIONS.map(([key, label]) => (
              <Button
                key={key}
                variant={direction === key ? 'save' : 'icon'}
                className={direction === key ? classes.toggle : `${classes.toggle} ${classes.toggleIdle}`}
                fullWidth
                disabled={isSaving}
                onClick={() => setDirection(key)}
                dataTestId={`cash-move-direction-${key}`}
              >
                {label}
              </Button>
            ))}
          </div>

          <div className={keypadClasses.amountRow}>
            <div className={keypadClasses.amountCell}>
              <span className={keypadClasses.amountLabel}>סכום</span>
              <span className={keypadClasses.amountValue} data-testid="cash-move-amount">₪ {buffer === '' ? '0' : buffer}</span>
            </div>
            <span className={classes.drawerName} data-testid="cash-move-drawer">{drawerName ?? 'ללא מגירה'}</span>
          </div>

          <NumericKeypad
            dataTestId="cash-move"
            keypadClassName={keypadClasses.keypad}
            keyClassName={keypadClasses.key}
            disabled={isSaving}
            onDigit={(digit) => setBuffer((prev) => appendDigit(prev, digit))}
            onBackspace={() => setBuffer((prev) => dropLastChar(prev))}
            backspaceDisabled={buffer === ''}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={{
              label: '.',
              className: keypadClasses.key,
              onClick: () => setBuffer((prev) => appendDot(prev)),
              ariaLabel: 'נקודה עשרונית',
            }}
          />

          <Textarea
            data-testid="cash-move-reason"
            aria-label="סיבה"
            placeholder="סיבה (חובה)"
            value={reason}
            maxLength={MAX_REASON}
            disabled={isSaving}
            onChange={(event) => setReason(event.currentTarget.value)}
            className={classes.reason}
            autosize
            minRows={2}
            maxRows={4}
          />

          <div className={keypadClasses.actions}>
            <Button variant="cancel" fullWidth onClick={onClose} disabled={isSaving} dataTestId="cash-move-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              onClick={handleConfirm}
              disabled={!isValid || isSaving}
              loading={isSaving}
              loadingLabel="שומר..."
              dataTestId="cash-move-confirm"
            >
              רשום תנועה
            </Button>
          </div>
        </div>
      </div>

      {slipAt && (
        <div className={classes.offscreen} aria-hidden="true">
          <CashMoveSlip
            header={header}
            direction={direction}
            amount={amount}
            reason={trimmedReason}
            drawerName={drawerName}
            employeeName={employeeName}
            at={slipAt}
            innerRef={slipRef}
          />
        </div>
      )}
    </div>
  );
};

export const CashMoveModal = React.memo(CashMoveModalComponent);
